import { useQuery } from '@tanstack/react-query'
import { useCallback, useEffect, useMemo, useState } from 'react'

import { useSessionContext } from 'src/hooks'

import { recallAgent, rememberAgent } from './agentSnapshot'
import { getSquadConfig, isSquadConfigured } from './squadAgent'
import { getAgent } from './squadApi'
import { registerSquadAppToken } from './squadAppTokenStore'
import { useChatSession } from './useChatSession'
import { useSquadAppToken } from './useSquadAppToken'
import { useSquadConversations } from './useSquadConversations'

const AGENT_STALE_TIME_MS = 10 * 60 * 1000

export const useSquadChat = (workspaceId: number | undefined) => {
  const { user } = useSessionContext()
  const configured = isSquadConfigured()
  const { agentId } = getSquadConfig()
  const appToken = useSquadAppToken(configured ? workspaceId : undefined)
  const enabled = configured && Boolean(appToken)

  useEffect(() => registerSquadAppToken(appToken), [appToken])

  // The agent's name and avatar, so the chat looks and reads like the agent it
  // speaks as instead of a generic assistant. Near-static, hence the long
  // staleTime; a failure just leaves the brand fallback in place.
  const agentQuery = useQuery({
    queryKey: ['squad-agent', agentId],
    queryFn: () => getAgent(appToken ?? '', agentId ?? ''),
    enabled: enabled && Boolean(agentId),
    staleTime: AGENT_STALE_TIME_MS
  })

  useEffect(() => {
    if (agentQuery.data) {
      rememberAgent(agentQuery.data)
    }
  }, [agentQuery.data])

  const glyphAgent = agentQuery.data ?? recallAgent(agentId)

  const [historyOpen, setHistoryOpen] = useState(false)

  const baseConversations = useSquadConversations(
    appToken ?? '',
    agentId,
    enabled,
    user?.user?.sso_id ?? undefined
  )
  const session = useChatSession({
    conversations: baseConversations,
    configured: enabled,
    appToken: appToken ?? '',
    agentId
  })

  const deleteConversation = useCallback(
    (conversationId: string) => {
      session.stop(conversationId)
      baseConversations.deleteConversation(conversationId)
    },
    [baseConversations, session]
  )

  const conversations = useMemo(
    () => ({ ...baseConversations, deleteConversation }),
    [baseConversations, deleteConversation]
  )

  return useMemo(
    () => ({
      configured,
      enabled,
      agentId,
      appToken,
      agent: agentQuery.data,
      glyphAgent,
      conversations,
      session,
      historyOpen,
      openHistory: () => setHistoryOpen(true),
      closeHistory: () => setHistoryOpen(false)
    }),
    [
      configured,
      enabled,
      agentId,
      appToken,
      agentQuery.data,
      glyphAgent,
      conversations,
      session,
      historyOpen
    ]
  )
}

export type SquadChat = ReturnType<typeof useSquadChat>
